<template>
  <div class="p-6">
    <!-- Encabezado -->
    <div class="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
      <div>
        <h1 class="text-2xl font-bold text-gray-800 dark:text-white">Gestión de Cajas</h1>
        <p class="text-gray-600 dark:text-gray-400">Administra las cajas del sistema</p>
      </div>
      <div class="flex items-center gap-3">
        <!-- Botón Exportar -->
        <div class="relative group">
          <button
            @click="showExportMenu = !showExportMenu"
            class="btn-secondary"
          >
            <Icon icon="mdi:file-export" />
            Exportar
            <Icon icon="mdi:chevron-down" />
          </button>
          
          <!-- Menú Exportar -->
          <div v-if="showExportMenu" 
               class="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 z-50">
            <div class="py-2">
              <button
                @click="exportToExcel"
                class="w-full px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center text-gray-700 dark:text-gray-300"
              >
                <Icon icon="mdi:microsoft-excel" class="mr-3 text-green-600" />
                <div>
                  <p class="font-medium">Exportar a Excel</p>
                  <p class="text-xs text-gray-500">Archivo .xlsx</p>
                </div>
              </button>
              <button
                @click="exportToPDF"
                class="w-full px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center text-gray-700 dark:text-gray-300"
              >
                <Icon icon="mdi:file-pdf-box" class="mr-3 text-red-600" />
                <div>
                  <p class="font-medium">Exportar a PDF</p>
                  <p class="text-xs text-gray-500">Documento .pdf</p>
                </div>
              </button>
              <div class="border-t border-gray-200 dark:border-gray-700 my-2"></div>
              <button
                @click="exportFullReport"
                class="w-full px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center text-gray-700 dark:text-gray-300"
              >
                <Icon icon="mdi:file-multiple" class="mr-3 text-blue-600" />
                <div>
                  <p class="font-medium">Reporte Completo</p>
                  <p class="text-xs text-gray-500">Excel + PDF</p>
                </div>
              </button>
            </div>
          </div>
        </div>
        
        <button
          v-if="!cajaAbiertaUsuario"
          @click="openAbrirCajaModal"
          class="btn-primary"
        >
          <Icon icon="mdi:cash-plus" />
          Abrir Caja
        </button>
        <button
          v-else
          @click="openCerrarCajaModal"
          class="btn-danger"
        >
          <Icon icon="mdi:cash-minus" />
          Cerrar Caja
        </button>
      </div>
    </div>

    <!-- Filtros y búsqueda -->
    <div class="premium-card p-4 mb-4">
      <div class="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
        <div class="col-span-12 md:col-span-5">
          <label class="label-premium">Buscar Cajas</label>
          <div class="relative">
            <Icon icon="mdi:magnify" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input v-model="searchQuery" type="text" placeholder="ID, observaciones..."
              class="input-premium pl-10 py-1.5 text-xs"
              @input="handleSearch" />
          </div>
        </div>
        <div class="col-span-6 md:col-span-3">
          <label class="label-premium">Estado</label>
          <select v-model="filterStatus" @change="handleFilterChange"
            class="input-premium py-1.5 text-xs cursor-pointer">
            <option value="all">Todas</option>
            <option value="abiertas">Abiertas</option>
            <option value="cerradas">Cerradas</option>
          </select>
        </div>
        <div class="col-span-6 md:col-span-2">
          <label class="label-premium">Ordenar Por</label>
          <select v-model="sortBy"
            class="input-premium py-1.5 text-xs cursor-pointer">
            <option value="fechaApertura_desc">Más recientes</option>
            <option value="fechaApertura">Más antiguas</option>
            <option value="montoInicial_desc">Mayor monto</option>
            <option value="montoInicial">Menor monto</option>
            <option value="estado">Estado</option>
          </select>
        </div>
        <div class="col-span-12 md:col-span-2 flex items-end">
          <button @click="loadCajas" class="w-full btn-secondary py-1.5 text-xs">
            <Icon icon="mdi:refresh" :class="{ 'animate-spin': loading }" />
            {{ loading ? 'Cargando...' : 'Actualizar' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Estado actual de caja -->
    <div v-if="cajaAbiertaUsuario" class="mb-6">
      <div class="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-700 rounded-2xl shadow-lg p-6">
        <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div class="flex items-center">
            <div class="p-4 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl shadow-lg mr-4 text-white">
              <Icon icon="mdi:cash-register" class="text-3xl" />
            </div>
            <div>
              <h3 class="text-xl font-bold text-gray-800 dark:text-white flex items-center">
                <Icon icon="mdi:check-circle" class="text-green-500 mr-2" />
                Caja abierta
              </h3>
              <p class="text-gray-600 dark:text-gray-400 mt-1">
                ID: <span class="font-bold text-gray-800 dark:text-white">#{{ cajaAbiertaUsuario.idCaja }}</span> | 
                Inició: <span class="font-bold text-gray-800 dark:text-white">{{ formatDateTime(cajaAbiertaUsuario.fechaApertura) }}</span>
              </p>
              <div class="flex flex-wrap gap-2 mt-3">
                <span class="px-3 py-1 bg-green-500/10 text-green-600 rounded-full text-xs font-bold uppercase">
                  Inicial: {{ formatearMonto(cajaAbiertaUsuario.montoInicial) }}
                </span>
                <span class="px-3 py-1 bg-blue-500/10 text-blue-600 rounded-full text-xs font-bold uppercase">
                  {{ calcularDuracion(cajaAbiertaUsuario.fechaApertura) }}
                </span>
              </div>
            </div>
          </div>
          <div class="flex flex-col sm:flex-row gap-3">
            <button @click="openResumenModal(cajaAbiertaUsuario)" class="btn-primary">
              <Icon icon="mdi:chart-box" />
              Resumen
            </button>
            <button @click="openCerrarCajaModal" class="btn-danger">
              <Icon icon="mdi:cash-minus" />
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Estadísticas -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      <div class="premium-card p-4 flex items-center gap-4">
        <div class="p-3 bg-blue-500/10 rounded-xl">
          <Icon icon="mdi:cash-register" class="text-blue-500 text-2xl" />
        </div>
        <div>
          <p class="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Total Cajas</p>
          <p class="text-xl font-black text-gray-800 dark:text-white leading-none">{{ cajas.length }}</p>
        </div>
      </div>

      <div class="premium-card p-4 flex items-center gap-4">
        <div class="p-3 bg-green-500/10 rounded-xl">
          <Icon icon="mdi:lock-open-variant" class="text-green-500 text-2xl" />
        </div>
        <div>
          <p class="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Abiertas</p>
          <p class="text-xl font-black text-gray-800 dark:text-white leading-none">{{ estadisticas.abiertas }}</p>
        </div>
      </div>

      <div class="premium-card p-4 flex items-center gap-4">
        <div class="p-3 bg-red-500/10 rounded-xl">
          <Icon icon="mdi:lock" class="text-red-500 text-2xl" />
        </div>
        <div>
          <p class="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Cerradas</p>
          <p class="text-xl font-black text-gray-800 dark:text-white leading-none">{{ estadisticas.cerradas }}</p>
        </div>
      </div>

      <div class="premium-card p-4 flex items-center gap-4">
        <div class="p-3 bg-purple-500/10 rounded-xl">
          <Icon icon="mdi:cash" class="text-purple-500 text-2xl" />
        </div>
        <div>
          <p class="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Total General</p>
          <p class="text-xl font-black text-gray-800 dark:text-white leading-none">{{ formatearMonto(estadisticas.totalGeneral) }}</p>
        </div>
      </div>
    </div>

    <!-- Indicadores de filtro -->
    <div v-if="isFiltered" class="mb-4">
      <div class="flex flex-wrap items-center gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3">
        <span class="text-sm font-medium text-amber-800 dark:text-amber-300">
          <Icon icon="mdi:filter" class="inline mr-1" />
          Filtros aplicados:
        </span>
        <span v-if="searchQuery" class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200">
          Buscar: "{{ searchQuery }}"
          <button @click="searchQuery = ''" class="ml-1.5 hover:text-blue-900">
            <Icon icon="mdi:close" class="text-xs" />
          </button>
        </span>
        <span v-if="filterStatus !== 'all'" class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-800 dark:text-indigo-200">
          Estado: {{ filterStatus === 'abiertas' ? 'Abiertas' : 'Cerradas' }}
          <button @click="filterStatus = 'all'; handleFilterChange()" class="ml-1.5 hover:text-indigo-900">
            <Icon icon="mdi:close" class="text-xs" />
          </button>
        </span>
        <button
          @click="clearFilters"
          class="text-sm text-amber-700 dark:text-amber-400 hover:text-amber-900 dark:hover:text-amber-300 font-medium"
        >
          <Icon icon="mdi:filter-off" class="inline mr-1" />
          Limpiar filtros
        </button>
      </div>
    </div>

    <!-- Tabla de cajas -->
    <div class="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden border border-gray-200 dark:border-gray-700">
      <!-- Encabezado de tabla -->
      <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
        <div>
          <h2 class="text-lg font-semibold text-gray-800 dark:text-white">Historial de Cajas</h2>
          <p class="text-sm text-gray-600 dark:text-gray-400">
            Mostrando {{ filteredCajas.length }} de {{ cajas.length }} cajas
          </p>
        </div>
        <div class="flex items-center space-x-2">
          <div class="text-sm text-gray-500 dark:text-gray-400">
            <Icon icon="mdi:calendar" class="inline mr-1" />
            Última actualización: {{ formatTime(lastUpdate) }}
          </div>
        </div>
      </div>

      <!-- Tabla -->
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead class="bg-gray-50 dark:bg-gray-700/50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                <div class="flex items-center space-x-1">
                  <span>Caja</span>
                </div>
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Apertura / Cierre
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Montos
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Totales
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Estado
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            <!-- Estado de carga -->
            <tr v-if="loading">
              <td colspan="6" class="px-6 py-12">
                <div class="flex flex-col items-center justify-center">
                  <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mb-4"></div>
                  <p class="text-gray-600 dark:text-gray-400">Cargando cajas...</p>
                </div>
              </td>
            </tr>

            <!-- Sin resultados -->
            <tr v-else-if="filteredCajas.length === 0">
              <td colspan="6" class="px-6 py-16 text-center">
                <div class="flex flex-col items-center">
                  <div class="p-4 bg-gray-100 dark:bg-gray-700 rounded-full mb-4">
                    <Icon icon="mdi:cash-register-off" class="text-3xl text-gray-400" />
                  </div>
                  <h3 class="text-lg font-medium text-gray-800 dark:text-white mb-2">
                    No se encontraron cajas
                  </h3>
                  <p class="text-gray-600 dark:text-gray-400 mb-6 max-w-md">
                    {{ searchQuery || filterStatus !== 'all'
                      ? 'Intenta con otros términos de búsqueda o cambia los filtros.' 
                      : 'No hay cajas registradas. ¡Abre la primera!' }}
                  </p>
                  <button
                    v-if="!searchQuery && filterStatus === 'all'"
                    @click="openAbrirCajaModal"
                    class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Icon icon="mdi:cash-plus" class="mr-2 inline" />
                    Abrir primera caja
                  </button>
                  <button
                    v-else
                    @click="clearFilters"
                    class="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                  >
                    <Icon icon="mdi:filter-off" class="mr-2 inline" />
                    Limpiar filtros
                  </button>
                </div>
              </td>
            </tr>

            <!-- Lista de cajas -->
            <tr 
              v-else 
              v-for="caja in paginatedCajas" 
              :key="caja.idCaja"
              class="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-150 cursor-pointer"
              @click="openDetailModal(caja)"
            >
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center">
                  <div class="flex-shrink-0 h-12 w-12">
                    <div :class="[
                      'h-12 w-12 rounded-xl flex items-center justify-center shadow-sm',
                      caja.estado?.toLowerCase() === 'abierta'
                        ? 'bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-900 dark:to-emerald-900'
                        : 'bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900'
                    ]">
                      <Icon 
                        icon="mdi:cash-register" 
                        :class="[
                          'text-xl',
                          caja.estado?.toLowerCase() === 'abierta'
                            ? 'text-green-600 dark:text-green-400'
                            : 'text-gray-500 dark:text-gray-400'
                        ]" 
                      />
                    </div>
                  </div>
                  <div class="ml-4">
                    <div class="text-sm font-semibold text-gray-900 dark:text-white">
                      Caja #{{ caja.idCaja }}
                      <span v-if="caja.estado?.toLowerCase() === 'cerrada'" class="ml-2 text-xs text-gray-500">
                        (Cerrada)
                      </span>
                    </div>
                    <div class="text-xs text-gray-500 dark:text-gray-400 flex items-center mt-1">
                      <Icon icon="mdi:account" class="mr-1" />
                      Usuario: {{ obtenerNombreUsuario(caja.idUsuario) }}
                    </div>
                  </div>
                </div>
              </td>
              <td class="px-6 py-4">
                <div class="space-y-1">
                  <div class="flex items-center text-sm text-gray-900 dark:text-white">
                    <Icon icon="mdi:calendar-arrow-right" class="mr-2 text-green-500" />
                    <span class="font-medium">Apertura:</span>
                    <span class="ml-2">{{ formatDateTime(caja.fechaApertura) }}</span>
                  </div>
                  <div v-if="caja.fechaCierre" class="flex items-center text-sm text-gray-900 dark:text-white">
                    <Icon icon="mdi:calendar-arrow-left" class="mr-2 text-red-500" />
                    <span class="font-medium">Cierre:</span>
                    <span class="ml-2">{{ formatDateTime(caja.fechaCierre) }}</span>
                  </div>
                  <div class="text-xs text-gray-500 dark:text-gray-400">
                    Duración: {{ calcularDuracion(caja.fechaApertura, caja.fechaCierre) }}
                  </div>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="space-y-2">
                  <div>
                    <span class="text-xs text-gray-500 dark:text-gray-400">Inicial:</span>
                    <p class="text-sm font-medium text-gray-900 dark:text-white">
                      {{ formatearMonto(caja.montoInicial) }}
                    </p>
                  </div>
                  <div v-if="caja.montoFinal !== null">
                    <span class="text-xs text-gray-500 dark:text-gray-400">Final:</span>
                    <p class="text-sm font-medium text-gray-900 dark:text-white">
                      {{ formatearMonto(caja.montoFinal) }}
                    </p>
                  </div>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="space-y-2">
                  <div>
                    <span class="text-xs text-gray-500 dark:text-gray-400">Ventas:</span>
                    <p class="text-sm font-medium text-green-600 dark:text-green-400">
                      {{ formatearMonto(caja.totalVentas) }}
                    </p>
                  </div>
                  <div>
                    <span class="text-xs text-gray-500 dark:text-gray-400">Servicios:</span>
                    <p class="text-sm font-medium text-blue-600 dark:text-blue-400">
                      {{ formatearMonto(caja.totalServicios) }}
                    </p>
                  </div>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span
                  @click.stop="toggleStatus(caja)"
                  :class="[
                    'px-3 py-1.5 inline-flex text-xs leading-4 font-semibold rounded-full shadow-sm transition-all duration-300 cursor-pointer',
                    caja.estado?.toLowerCase() === 'abierta'
                      ? 'bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900 dark:to-emerald-900 text-green-800 dark:text-green-200 hover:from-green-200 hover:to-emerald-200 dark:hover:from-green-800 dark:hover:to-emerald-800'
                      : 'bg-gradient-to-r from-red-100 to-pink-100 dark:from-red-900 dark:to-pink-900 text-red-800 dark:text-red-200 hover:from-red-200 hover:to-pink-200 dark:hover:from-red-800 dark:hover:to-pink-800'
                  ]"
                >
                  <Icon :icon="caja.estado?.toLowerCase() === 'abierta' ? 'mdi:lock-open-variant' : 'mdi:lock'" class="mr-1.5" />
                  {{ caja.estado || 'Sin estado' }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <div class="flex items-center space-x-2">
                  <button
                    @click.stop="openResumenModal(caja)"
                    class="p-2 bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-800/50 transition-colors"
                    title="Ver Resumen"
                  >
                    <Icon icon="mdi:chart-box" />
                  </button>
                  <button
                    v-if="caja.estado?.toLowerCase() === 'abierta'"
                    @click.stop="openCerrarCajaModal(caja)"
                    class="p-2 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-800/50 transition-colors"
                    title="Cerrar Caja"
                  >
                    <Icon icon="mdi:cash-minus" />
                  </button>
                  <button
                    v-else
                    @click.stop="openReabrirCajaModal(caja)"
                    class="p-2 bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-lg hover:bg-green-100 dark:hover:bg-green-800/50 transition-colors"
                    title="Reabrir Caja"
                  >
                    <Icon icon="mdi:cash-plus" />
                  </button>
                  <button
                    @click.stop="confirmDelete(caja)"
                    class="p-2 bg-gray-50 dark:bg-gray-900/30 text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800/50 transition-colors"
                    title="Eliminar"
                  >
                    <Icon icon="mdi:delete" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Paginación Premium -->
      <div v-if="filteredCajas.length > 0 && !loading" class="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-3">
        <div class="text-xs text-gray-500 dark:text-gray-400">
          Mostrando <span class="font-semibold text-gray-700 dark:text-gray-300">{{ startItem }}–{{ endItem }}</span> de <span class="font-semibold text-gray-700 dark:text-gray-300">{{ filteredCajas.length }}</span>
        </div>
        <div class="flex items-center gap-1">
          <button @click="currentPage = Math.max(currentPage - 1, 1)" :disabled="currentPage === 1"
            class="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 dark:border-gray-600 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all">
            <Icon icon="mdi:chevron-left" class="text-base" />
          </button>
          <button v-for="page in visiblePages" :key="page" @click="currentPage = page"
            :class="['w-8 h-8 flex items-center justify-center rounded-lg text-xs font-semibold transition-all',
              currentPage === page
                ? 'bg-blue-600 text-white shadow-sm shadow-blue-300 dark:shadow-blue-900'
                : 'border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700']">{{ page }}</button>
          <button @click="currentPage = Math.min(currentPage + 1, totalPages)" :disabled="currentPage === totalPages"
            class="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 dark:border-gray-600 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all">
            <Icon icon="mdi:chevron-right" class="text-base" />
          </button>
        </div>
      </div>
    </div>

    <!-- Modal para abrir caja -->
    <BaseModal 
      v-model="showAbrirModal" 
      title="Abrir Caja"
      icon="mdi:cash-plus"
      iconType="success"
      maxWidth="max-w-lg"
    >
      <form @submit.prevent="abrirCaja" class="space-y-6">
        <div>
          <label class="label-premium">Usuario *</label>
          <div class="relative">
            <Icon icon="mdi:account" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <select v-model="formAbrir.idUsuario" required class="input-premium pl-10 cursor-pointer">
              <option value="">Seleccione un usuario</option>
              <option v-for="usuario in usuariosActivos" :key="usuario.idUsuario" :value="usuario.idUsuario">
                {{ usuario.nombre }} {{ usuario.apellido }} ({{ usuario.nombreUsuario }})
              </option>
            </select>
          </div>
          <p v-if="errorsAbrir.idUsuario" class="mt-2 text-xs text-rose-500 font-bold uppercase tracking-wider">{{ errorsAbrir.idUsuario }}</p>
        </div>
        
        <div>
          <label class="label-premium">Monto Inicial (Bs.) *</label>
          <div class="relative">
            <Icon icon="mdi:cash" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input v-model="formAbrir.montoInicial" type="number" required step="0.01" min="0" class="input-premium pl-10" placeholder="0.00" />
          </div>
          <p v-if="errorsAbrir.montoInicial" class="mt-2 text-xs text-rose-500 font-bold uppercase tracking-wider">{{ errorsAbrir.montoInicial }}</p>
        </div>
        
        <div>
          <label class="label-premium">Observaciones</label>
          <textarea v-model="formAbrir.observaciones" rows="3" maxlength="500" class="input-premium resize-none" placeholder="Observaciones iniciales..."></textarea>
          <div class="text-right mt-1">
            <span class="text-[10px] text-gray-400 font-bold">{{ formAbrir.observaciones.length }}/500</span>
          </div>
        </div>
        
        <div class="flex gap-3 pt-4">
          <button type="button" @click="closeAbrirModal" class="flex-1 btn-secondary">Cancelar</button>
          <button type="submit" :disabled="saving || !isFormAbrirValid" class="flex-1 btn-primary">
            <Icon v-if="saving" icon="mdi:loading" class="animate-spin" />
            {{ saving ? 'Abriendo...' : 'Abrir Caja' }}
          </button>
        </div>
      </form>
    </BaseModal>

    <!-- Modal para cerrar caja -->
    <BaseModal 
      v-model="showCerrarModal" 
      title="Cerrar Caja"
      icon="mdi:cash-minus"
      iconType="danger"
      maxWidth="max-w-lg"
    >
      <form @submit.prevent="cerrarCaja" class="space-y-6">
        <div class="p-4 bg-rose-500/10 border border-rose-500/20 rounded-xl">
          <div class="flex items-center gap-3">
            <div class="p-2 bg-rose-500 rounded-lg text-white">
              <Icon icon="mdi:cash-register" />
            </div>
            <div>
              <p class="font-bold text-gray-800 dark:text-white leading-none">Caja #{{ cajaSeleccionada?.idCaja }}</p>
              <p class="text-[10px] text-gray-500 uppercase font-bold mt-1 tracking-wider">Abierta: {{ formatDateTime(cajaSeleccionada?.fechaApertura) }}</p>
            </div>
          </div>
        </div>
        
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div class="col-span-full">
            <label class="label-premium">Monto Final en Caja (Bs.) *</label>
            <div class="relative">
              <Icon icon="mdi:cash" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input v-model="formCerrar.montoFinal" type="number" required step="0.01" min="0" class="input-premium pl-10 border-rose-200 focus:ring-rose-500/20" placeholder="0.00" />
            </div>
            <p v-if="errorsCerrar.montoFinal" class="mt-2 text-xs text-rose-500 font-bold uppercase">{{ errorsCerrar.montoFinal }}</p>
          </div>
          
          <div>
            <label class="label-premium">Total Ventas (Bs.)</label>
            <input v-model="formCerrar.totalVentas" type="number" step="0.01" min="0" class="input-premium" placeholder="0.00" />
          </div>
          
          <div>
            <label class="label-premium">Total Servicios (Bs.)</label>
            <input v-model="formCerrar.totalServicios" type="number" step="0.01" min="0" class="input-premium" placeholder="0.00" />
          </div>
        </div>
        
        <div>
          <label class="label-premium">Observaciones de Cierre</label>
          <textarea v-model="formCerrar.observaciones" rows="3" class="input-premium resize-none" placeholder="Observaciones de cierre..."></textarea>
        </div>
        
        <div class="flex gap-3 pt-4">
          <button type="button" @click="closeCerrarModal" class="flex-1 btn-secondary">Cancelar</button>
          <button type="submit" :disabled="saving || !isFormCerrarValid" class="flex-1 btn-danger">
            <Icon v-if="saving" icon="mdi:loading" class="animate-spin" />
            {{ saving ? 'Cerrando...' : 'Cerrar Caja' }}
          </button>
        </div>
      </form>
    </BaseModal>

    <!-- Modal de Resumen -->
    <BaseModal 
      v-model="showResumenModal" 
      title="Resumen de Caja"
      icon="mdi:chart-box"
      iconType="primary"
      maxWidth="max-w-xl"
    >
      <div v-if="cajaSeleccionada" class="space-y-6">
        <div class="grid grid-cols-2 gap-4">
          <div class="p-4 premium-card text-center">
            <p class="text-[10px] text-gray-400 font-bold uppercase mb-1">Monto Inicial</p>
            <p class="text-xl font-black text-gray-800 dark:text-white">{{ formatearMonto(cajaSeleccionada.montoInicial) }}</p>
          </div>
          <div class="p-4 premium-card text-center">
            <p class="text-[10px] text-gray-400 font-bold uppercase mb-1">Ingresos Totales</p>
            <p class="text-xl font-black text-emerald-500">{{ formatearMonto(cajaSeleccionada.totalVentas + cajaSeleccionada.totalServicios) }}</p>
          </div>
        </div>

        <div class="space-y-3">
          <div class="flex justify-between items-center p-3 premium-card">
            <div class="flex items-center gap-3">
              <div class="p-2 bg-green-500/10 text-green-500 rounded-lg"><Icon icon="mdi:shopping" /></div>
              <span class="text-sm font-bold text-gray-600 dark:text-gray-400">Ventas de Productos</span>
            </div>
            <span class="font-bold text-gray-800 dark:text-white">{{ formatearMonto(cajaSeleccionada.totalVentas) }}</span>
          </div>
          <div class="flex justify-between items-center p-3 premium-card">
            <div class="flex items-center gap-3">
              <div class="p-2 bg-blue-500/10 text-blue-500 rounded-lg"><Icon icon="mdi:wrench" /></div>
              <span class="text-sm font-bold text-gray-600 dark:text-gray-400">Servicios Técnicos</span>
            </div>
            <span class="font-bold text-gray-800 dark:text-white">{{ formatearMonto(cajaSeleccionada.totalServicios) }}</span>
          </div>
        </div>

        <div class="flex gap-3 pt-4">
          <button @click="closeResumenModal" class="flex-1 btn-secondary">Cerrar</button>
          <button @click="exportToPDF" class="flex-1 btn-primary"><Icon icon="mdi:printer" /> Imprimir</button>
        </div>
      </div>
    </BaseModal>
    <!-- Modal de Detalles -->
    <BaseModal
      v-model="showDetailModal"
      title="Detalles de la Caja"
      icon="mdi:cash-register"
      iconType="primary"
      maxWidth="max-w-md"
    >
      <div v-if="selectedCaja" class="space-y-6">
        <div class="flex items-center space-x-4">
          <div class="p-4 rounded-xl bg-gradient-to-r from-purple-500 to-indigo-500">
            <Icon icon="mdi:cash-register" class="text-white text-2xl" />
          </div>
          <div>
            <h4 class="text-lg font-semibold text-gray-900 dark:text-white">Caja #{{ selectedCaja.idCaja }}</h4>
            <p class="text-sm text-gray-500 dark:text-gray-400">
              Usuario: {{ obtenerNombreUsuario(selectedCaja.idUsuario) }}
            </p>
          </div>
        </div>
        
        <div class="space-y-4">
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Estado</label>
              <p :class="[
                'mt-1 px-3 py-1.5 rounded-full inline-block',
                selectedCaja.estado?.toLowerCase() === 'abierta'
                  ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                  : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
              ]">
                {{ selectedCaja.estado || 'Sin estado' }}
              </p>
            </div>
            <div>
              <label class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Duración</label>
              <p class="mt-1 font-medium text-gray-900 dark:text-white">
                {{ calcularDuracion(selectedCaja.fechaApertura, selectedCaja.fechaCierre) }}
              </p>
            </div>
          </div>
          
          <div>
            <label class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Monto Inicial</label>
            <p class="mt-1 text-xl font-bold text-gray-900 dark:text-white">{{ formatearMonto(selectedCaja.montoInicial) }}</p>
          </div>
          
          <div v-if="selectedCaja.montoFinal !== null">
            <label class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Monto Final</label>
            <p class="mt-1 text-xl font-bold text-gray-900 dark:text-white">{{ formatearMonto(selectedCaja.montoFinal) }}</p>
          </div>
          
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total Ventas</label>
              <p class="mt-1 text-lg font-bold text-green-600 dark:text-green-400">{{ formatearMonto(selectedCaja.totalVentas) }}</p>
            </div>
            <div>
              <label class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total Servicios</label>
              <p class="mt-1 text-lg font-bold text-blue-600 dark:text-blue-400">{{ formatearMonto(selectedCaja.totalServicios) }}</p>
            </div>
          </div>
          
          <div>
            <label class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Fecha de Apertura</label>
            <div class="mt-1 flex items-center space-x-2 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <Icon icon="mdi:calendar-arrow-right" class="text-green-500" />
              <div>
                <p class="text-sm text-gray-900 dark:text-white">{{ formatDate(selectedCaja.fechaApertura) }}</p>
                <p class="text-xs text-gray-500 dark:text-gray-400">{{ formatTime(selectedCaja.fechaApertura) }}</p>
              </div>
            </div>
          </div>
          
          <div v-if="selectedCaja.fechaCierre">
            <label class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Fecha de Cierre</label>
            <div class="mt-1 flex items-center space-x-2 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <Icon icon="mdi:calendar-arrow-left" class="text-red-500" />
              <div>
                <p class="text-sm text-gray-900 dark:text-white">{{ formatDate(selectedCaja.fechaCierre) }}</p>
                <p class="text-xs text-gray-500 dark:text-gray-400">{{ formatTime(selectedCaja.fechaCierre) }}</p>
              </div>
            </div>
          </div>
          
          <div v-if="selectedCaja.observaciones">
            <label class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Observaciones</label>
            <p class="mt-1 text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
              {{ selectedCaja.observaciones }}
            </p>
          </div>
          
          <div class="pt-4 border-t border-gray-200 dark:border-gray-700">
            <div class="flex justify-end space-x-3">
              <button
                @click="openResumenModal(selectedCaja); closeDetailModal()"
                class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                type="button"
              >
                Ver Resumen
              </button>
              <button
                @click="closeDetailModal"
                class="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                type="button"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      </div>
    </BaseModal>
  </div>
</template>

<script>
import { Icon } from '@iconify/vue';
import { useToast } from "vue-toastification";
import { useModalStore } from "@/store/modal";
import BaseModal from "@/components/common/BaseModal.vue";
import { debounce } from 'lodash';

// Importar funciones de la API de cajas
import {
  obtenerCajas,
  obtenerCajaAbiertaPorUsuario,
  abrirCaja,
  cerrarCaja,
  eliminarCaja,
  cambiarEstadoCaja,
  filtrarCajasPorEstado,
  ordenarCajas,
  buscarCajasPorTermino,
  obtenerEstadisticasCajas,
  exportarExcelCajas,
  exportarPDFCajas,
  exportarReporteCompletoCajas,
  formatearMonto,
  calcularDuracionCaja
} from '@/api/cajas.js';

// Importar funciones de la API de usuarios
import { obtenerUsuarios } from '@/api/usuarios.js';

export default {
  name: 'CajasList',
  components: {
    Icon,
    BaseModal
  },
  setup() {
    const toast = useToast();
    const modal = useModalStore();
    return { toast, modal };
  },
  data() {
    return {
      cajas: [],
      usuarios: [], // Lista de usuarios
      loading: false,
      saving: false,
      showAbrirModal: false,
      showCerrarModal: false,
      showResumenModal: false,
      showDetailModal: false,
      showExportMenu: false,
      searchQuery: '',
      filterStatus: 'all',
      sortBy: 'fechaApertura_desc',
      currentPage: 1,
      itemsPerPage: 10,
      cajaAbiertaUsuario: null,
      cajaSeleccionada: null,
      selectedCaja: null,
      formAbrir: {
        idUsuario: '',
        montoInicial: '',
        observaciones: '',
        estado: 'Abierta'
      },
      formCerrar: {
        montoFinal: '',
        totalVentas: '',
        totalServicios: '',
        observaciones: ''
      },
      errorsAbrir: {},
      errorsCerrar: {},
      lastUpdate: new Date()
    };
  },
  computed: {
    filteredCajas() {
      let filtered = [...this.cajas];
      
      // Aplicar búsqueda
      if (this.searchQuery.trim()) {
        filtered = buscarCajasPorTermino(filtered, this.searchQuery);
      }
      
      // Aplicar filtro de estado
      filtered = filtrarCajasPorEstado(filtered, this.filterStatus);
      
      // Aplicar ordenamiento
      filtered = ordenarCajas(filtered, this.sortBy);
      
      return filtered;
    },
    
    estadisticas() {
      return obtenerEstadisticasCajas(this.cajas);
    },
    
    paginatedCajas() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredCajas.slice(start, end);
    },
    
    totalPages() {
      return Math.ceil(this.filteredCajas.length / this.itemsPerPage);
    },
    
    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);
      
      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    },
    
    startItem() {
      return (this.currentPage - 1) * this.itemsPerPage + 1;
    },
    
    endItem() {
      return Math.min(this.currentPage * this.itemsPerPage, this.filteredCajas.length);
    },
    
    isFormAbrirValid() {
      return this.formAbrir.idUsuario && this.formAbrir.montoInicial;
    },
    
    isFormCerrarValid() {
      return this.formCerrar.montoFinal;
    },
    
    isFiltered() {
      return this.searchQuery.trim() || this.filterStatus !== 'all';
    },
    
    // Filtrar usuarios activos para el select
    usuariosActivos() {
      return this.usuarios.filter(u => u.estado?.toLowerCase() === 'activo');
    }
  },
  mounted() {
    this.loadUsuarios();
    this.loadCajas();
  },
  watch: {
    filterStatus() {
      this.currentPage = 1;
    },
    searchQuery() {
      this.currentPage = 1;
    },
    sortBy() {
      this.currentPage = 1;
    },
  },
  methods: {
    async loadUsuarios() {
      try {
        console.log('👥 Cargando usuarios...');
        const response = await obtenerUsuarios('activos');
        
        if (response.success) {
          this.usuarios = response.data || [];
          console.log('✅ Usuarios cargados:', this.usuarios.length);
        } else {
          console.warn('⚠️ No se pudieron cargar usuarios:', response.error);
          // Si falla, mostrar un usuario de ejemplo
          this.usuarios = [
            { idUsuario: 1, nombre: 'Usuario', apellido: 'Demo', nombreUsuario: 'demo', estado: 'activo' }
          ];
        }
      } catch (error) {
        console.error('❌ Error cargando usuarios:', error);
        // Usuario de respaldo
        this.usuarios = [
          { idUsuario: 1, nombre: 'Usuario', apellido: 'Demo', nombreUsuario: 'demo', estado: 'activo' }
        ];
      }
    },
    
    async loadCajas() {
      this.loading = true;
      try {
        console.log('📋 Cargando cajas...');
        const response = await obtenerCajas(this.filterStatus);
        
        if (response.success) {
          this.cajas = response.data || [];
          this.lastUpdate = new Date();
          console.log('✅ Cajas cargadas:', this.cajas.length);
          
          // Verificar si hay caja abierta para el usuario actual (si hay un usuario logeado)
          // Esto debería venir de tu store de autenticación
          const usuarioLogeadoId = this.getUsuarioLogeadoId();
          if (usuarioLogeadoId) {
            await this.checkCajaAbierta(usuarioLogeadoId);
          }
        } else {
          throw new Error(response.error || 'Error al cargar cajas');
        }
      } catch (error) {
        console.error('❌ Error al cargar cajas:', error);
        this.showErrorAlert('Error al cargar las cajas: ' + error.message);
      } finally {
        this.loading = false;
      }
    },
    
    getUsuarioLogeadoId() {
      // Esto debería obtener el ID del usuario logeado desde tu store (Vuex/Pinia) o localStorage
      // Por ahora, devuelve null o el primer usuario activo para demostración
      try {
        const userData = localStorage.getItem('user');
        if (userData) {
          const user = JSON.parse(userData);
          return user.idUsuario || null;
        }
      } catch (e) {
        console.warn('No hay usuario logeado en localStorage');
      }
      return null;
    },
    
    async checkCajaAbierta(userId) {
      try {
        if (!userId) return;
        
        console.log(`🔍 Verificando caja abierta para usuario ${userId}`);
        const response = await obtenerCajaAbiertaPorUsuario(userId);
        
        if (response.success && response.data) {
          this.cajaAbiertaUsuario = response.data;
          console.log('✅ Caja abierta encontrada:', this.cajaAbiertaUsuario.idCaja);
        } else {
          this.cajaAbiertaUsuario = null;
          console.log('ℹ️ No hay caja abierta para este usuario');
        }
      } catch (error) {
        console.error('❌ Error al verificar caja abierta:', error);
        this.cajaAbiertaUsuario = null;
      }
    },
    
    handleFilterChange() {
      this.currentPage = 1;
      this.loadCajas();
    },
    
    handleSearch: debounce(function() {
      this.currentPage = 1;
    }, 300),
    
    openAbrirCajaModal() {
      // Verificar si hay usuarios disponibles
      if (this.usuariosActivos.length === 0) {
        this.showErrorAlert('No hay usuarios activos disponibles. Debes crear usuarios primero.');
        return;
      }
      
      this.formAbrir = {
        idUsuario: this.usuariosActivos[0]?.idUsuario || '',
        montoInicial: '',
        observaciones: '',
        estado: 'Abierta'
      };
      this.errorsAbrir = {};
      this.showAbrirModal = true;
    },
    
    closeAbrirModal() {
      this.showAbrirModal = false;
      this.formAbrir = {
        idUsuario: '',
        montoInicial: '',
        observaciones: '',
        estado: 'Abierta'
      };
      this.errorsAbrir = {};
    },
    
    validateAbrirForm() {
      this.errorsAbrir = {};
      let isValid = true;
      
      if (!this.formAbrir.idUsuario) {
        this.errorsAbrir.idUsuario = 'Seleccione un usuario';
        isValid = false;
      } else {
        // Verificar si el usuario ya tiene una caja abierta
        const usuario = this.usuarios.find(u => u.idUsuario == this.formAbrir.idUsuario);
        const cajaUsuarioAbierta = this.cajas.find(c => 
          c.idUsuario == this.formAbrir.idUsuario && 
          c.estado?.toLowerCase() === 'abierta'
        );
        
        if (cajaUsuarioAbierta) {
          this.errorsAbrir.idUsuario = `El usuario ${usuario?.nombre || 'seleccionado'} ya tiene una caja abierta (#${cajaUsuarioAbierta.idCaja})`;
          isValid = false;
        }
      }
      
      if (!this.formAbrir.montoInicial) {
        this.errorsAbrir.montoInicial = 'El monto inicial es requerido';
        isValid = false;
      } else if (parseFloat(this.formAbrir.montoInicial) < 0) {
        this.errorsAbrir.montoInicial = 'El monto inicial no puede ser negativo';
        isValid = false;
      }
      
      return isValid;
    },
    
    async abrirCaja() {
      if (!this.validateAbrirForm()) {
        return;
      }

      this.saving = true;
      try {
        const cajaData = {
          idUsuario: parseInt(this.formAbrir.idUsuario),
          montoInicial: parseFloat(this.formAbrir.montoInicial),
          observaciones: this.formAbrir.observaciones.trim(),
          estado: this.formAbrir.estado
        };
        
        console.log('🚪 Abriendo caja:', cajaData);
        const result = await abrirCaja(cajaData);
        
        if (result.success) {
          await this.loadCajas();
          
          // Actualizar la caja abierta del usuario
          const usuarioId = parseInt(this.formAbrir.idUsuario);
          await this.checkCajaAbierta(usuarioId);
          
          this.closeAbrirModal();
          this.toast.success('Caja abierta correctamente');
        } else {
          throw new Error(result.error || 'Error al abrir caja');
        }
      } catch (error) {
        console.error('❌ Error al abrir caja:', error);
        this.toast.error(error.message || 'Error al abrir la caja');
      } finally {
        this.saving = false;
      }
    },
    
    openCerrarCajaModal(caja = null) {
      this.cajaSeleccionada = caja || this.cajaAbiertaUsuario;
      this.formCerrar = {
        montoFinal: '',
        totalVentas: caja?.totalVentas || '0',
        totalServicios: caja?.totalServicios || '0',
        observaciones: ''
      };
      this.errorsCerrar = {};
      this.showCerrarModal = true;
    },
    
    closeCerrarModal() {
      this.showCerrarModal = false;
      this.cajaSeleccionada = null;
      this.formCerrar = {
        montoFinal: '',
        totalVentas: '',
        totalServicios: '',
        observaciones: ''
      };
      this.errorsCerrar = {};
    },
    
    validateCerrarForm() {
      this.errorsCerrar = {};
      let isValid = true;
      
      if (!this.formCerrar.montoFinal) {
        this.errorsCerrar.montoFinal = 'El monto final es requerido';
        isValid = false;
      } else if (parseFloat(this.formCerrar.montoFinal) < 0) {
        this.errorsCerrar.montoFinal = 'El monto final no puede ser negativo';
        isValid = false;
      }
      
      return isValid;
    },
    
    async cerrarCaja() {
      if (!this.validateCerrarForm()) {
        return;
      }

      if (!this.cajaSeleccionada) {
        this.showErrorAlert('No hay caja seleccionada para cerrar');
        return;
      }

      this.saving = true;
      try {
        const cierreData = {
          montoFinal: parseFloat(this.formCerrar.montoFinal),
          totalVentas: parseFloat(this.formCerrar.totalVentas || 0),
          totalServicios: parseFloat(this.formCerrar.totalServicios || 0),
          observaciones: this.formCerrar.observaciones.trim()
        };
        
        console.log(`🔒 Cerrando caja ${this.cajaSeleccionada.idCaja}:`, cierreData);
        const result = await cerrarCaja(this.cajaSeleccionada.idCaja, cierreData);
        
        if (result.success) {
          await this.loadCajas();
          
          // Actualizar la caja abierta del usuario
          const usuarioId = this.cajaSeleccionada.idUsuario;
          await this.checkCajaAbierta(usuarioId);
          
          this.closeCerrarModal();
          this.toast.success('Caja cerrada correctamente');
        } else {
          throw new Error(result.error || 'Error al cerrar caja');
        }
      } catch (error) {
        console.error('❌ Error al cerrar caja:', error);
        this.toast.error(error.message || 'Error al cerrar la caja');
      } finally {
        this.saving = false;
      }
    },
    
    openResumenModal(caja) {
      this.cajaSeleccionada = caja;
      this.showResumenModal = true;
    },
    
    closeResumenModal() {
      this.showResumenModal = false;
      this.cajaSeleccionada = null;
    },
    
    async openReabrirCajaModal(caja) {
      const confirmed = await this.modal.confirm({
        title: '¿Reabrir caja?',
        message: `¿Deseas reabrir la caja <strong>#${caja.idCaja}</strong>?`,
        icon: 'mdi:help-circle',
        iconType: 'success',
        confirmText: 'Sí, reabrir'
      });

      if (confirmed) {
        try {
          const toggleResult = await cambiarEstadoCaja(caja.idCaja, 'Abierta');
          if (toggleResult.success) {
            await this.loadCajas();
            this.toast.success('La caja ha sido reabierta correctamente');
          } else {
            throw new Error(toggleResult.error || 'Error al reabrir caja');
          }
        } catch (error) {
          console.error('❌ Error al reabrir caja:', error);
          this.toast.error('Error al reabrir la caja');
        }
      }
    },
    
    openDetailModal(caja) {
      this.selectedCaja = caja;
      this.showDetailModal = true;
    },
    
    closeDetailModal() {
      this.showDetailModal = false;
      this.selectedCaja = null;
    },
    
    async toggleStatus(caja) {
      const esAbierta = caja.estado?.toLowerCase() === 'abierta';
      
      const confirmed = await this.modal.confirm({
        title: `¿${esAbierta ? 'Cerrar' : 'Abrir'} caja?`,
        message: `¿Deseas ${esAbierta ? 'cerrar' : 'abrir'} la caja <strong>#${caja.idCaja}</strong>?`,
        icon: 'mdi:help-circle',
        iconType: esAbierta ? 'warning' : 'success',
        confirmText: `Sí, ${esAbierta ? 'cerrar' : 'abrir'}`,
        type: esAbierta ? 'warning' : 'success'
      });

      if (confirmed) {
        try {
          const nuevoEstado = esAbierta ? 'Cerrada' : 'Abierta';
          const toggleResult = await cambiarEstadoCaja(caja.idCaja, nuevoEstado);
          
          if (toggleResult.success) {
            await this.loadCajas();
            if (esAbierta) {
              const usuarioId = this.getUsuarioLogeadoId();
              if (usuarioId && caja.idUsuario == usuarioId) {
                await this.checkCajaAbierta(usuarioId);
              }
            }
            this.toast.success(`La caja ha sido ${esAbierta ? 'cerrada' : 'abierta'} correctamente`);
          } else {
            throw new Error(toggleResult.error || 'Error al cambiar estado');
          }
        } catch (error) {
          console.error('❌ Error al cambiar estado:', error);
          this.toast.error('Error al cambiar el estado de la caja');
        }
      }
    },
    
    async confirmDelete(caja) {
      const confirmed = await this.modal.confirm({
        title: '¿Eliminar caja?',
        message: `¿Estás seguro de eliminar la caja <strong>#${caja.idCaja}</strong>? Esta acción no se puede deshacer.`,
        icon: 'mdi:alert-circle',
        iconType: 'danger',
        confirmText: 'Sí, eliminar',
        type: 'danger'
      });

      if (confirmed) {
        await this.deleteCaja(caja.idCaja);
      }
    },
    
    async deleteCaja(id) {
      try {
        const result = await eliminarCaja(id);
        if (result.success) {
          await this.loadCajas();
          this.toast.success(`La caja #${id} ha sido eliminada`);
        } else {
          throw new Error(result.error || 'Error al eliminar');
        }
      } catch (error) {
        console.error('❌ Error al eliminar caja:', error);
        this.toast.error(error.message || 'Error al eliminar la caja');
      }
    },
    
    // Métodos auxiliares para usuarios
    obtenerNombreUsuario(idUsuario) {
      if (!idUsuario) return 'Desconocido';
      const usuario = this.usuarios.find(u => u.idUsuario == idUsuario);
      if (!usuario) return `Usuario ${idUsuario}`;
      return `${usuario.nombre} ${usuario.apellido} (${usuario.nombreUsuario})`;
    },
    
    // Exportar funciones
    async exportToExcel() {
      if (this.filteredCajas.length === 0) {
        this.showWarningAlert('No hay datos para exportar');
        return;
      }

      try {
        const result = exportarExcelCajas(this.filteredCajas, 'cajas_system_jj');
        if (result.success) {
          this.showSuccessAlert('¡Exportado!', `Archivo ${result.filename} generado exitosamente`);
        } else {
          throw new Error(result.error);
        }
      } catch (error) {
        console.error('❌ Error exportando a Excel:', error);
        this.showErrorAlert('Error al exportar a Excel: ' + error.message);
      }
    },
    
    async exportToPDF() {
      if (this.filteredCajas.length === 0) {
        this.showWarningAlert('No hay datos para exportar');
        return;
      }

      try {
        const result = exportarPDFCajas(this.filteredCajas, 'Reporte de Cajas - SYSTEM JJ');
        if (result.success) {
          this.showSuccessAlert('¡Exportado!', `Documento ${result.filename} generado exitosamente`);
        } else {
          throw new Error(result.error);
        }
      } catch (error) {
        console.error('❌ Error exportando a PDF:', error);
        this.showErrorAlert('Error al exportar a PDF: ' + error.message);
      }
    },
    
    async exportFullReport() {
      if (this.filteredCajas.length === 0) {
        this.showWarningAlert('No hay datos para exportar');
        return;
      }

      try {
        const result = await exportarReporteCompletoCajas(this.filteredCajas, 'reporte_completo_cajas');
        if (result.success) {
          this.showSuccessAlert('¡Reportes generados!', 'Excel y PDF generados exitosamente');
        } else {
          throw new Error(result.error);
        }
      } catch (error) {
        console.error('❌ Error generando reporte completo:', error);
        this.showErrorAlert('Error al generar reporte: ' + error.message);
      }
    },
    
    // Métodos auxiliares
    clearFilters() {
      this.searchQuery = '';
      this.filterStatus = 'all';
      this.sortBy = 'fechaApertura_desc';
      this.currentPage = 1;
      this.loadCajas();
    },
    
    formatDate(fechaISO) {
      if (!fechaISO) return '';
      try {
        const fecha = new Date(fechaISO);
        return fecha.toLocaleDateString('es-ES', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      } catch (error) {
        return fechaISO;
      }
    },
    
    formatTime(fechaISO) {
      if (!fechaISO) return '';
      try {
        const fecha = new Date(fechaISO);
        return fecha.toLocaleTimeString('es-ES', {
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (error) {
        return '';
      }
    },
    
    formatDateTime(fechaISO) {
      if (!fechaISO) return '';
      try {
        const fecha = new Date(fechaISO);
        return fecha.toLocaleString('es-ES', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (error) {
        return fechaISO;
      }
    },
    
    formatearMonto(monto) {
      return formatearMonto(monto);
    },
    
    calcularDuracion(fechaApertura, fechaCierre = null) {
      return calcularDuracionCaja(fechaApertura, fechaCierre);
    },
    
    calcularDuracion(fechaApertura, fechaCierre = null) {
      return calcularDuracionCaja(fechaApertura, fechaCierre);
    },
  },
};
</script>

<style scoped>
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

.fade-in {
  animation: fadeIn 0.3s ease-out;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.dark ::-webkit-scrollbar-track {
  background: #374151;
}

.dark ::-webkit-scrollbar-thumb {
  background: #6b7280;
}

.dark ::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}
</style>